import { Component, ChangeDetectionStrategy, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ParsedDescription } from '../../../../models/ticket.models';

@Component({
  selector: 'app-parsed-description',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (parsed(); as desc) {
      @for (section of desc.sections; track section.title; let i = $index) {
        <div class="section-card mb-2" [class.expanded]="isExpanded(i)">
          <div
            class="section-header d-flex justify-content-between align-items-center"
            role="button"
            (click)="toggleSection(i)"
          >
            <span class="section-title">{{ section.title }}</span>
            <span class="toggle-icon">{{ isExpanded(i) ? '−' : '+' }}</span>
          </div>
          @if (isExpanded(i)) {
            <div class="section-body">
              <!-- Key-value pairs -->
              @if (section.keyValues.length > 0) {
                @for (kv of section.keyValues; track kv.key; let last = $last) {
                  <div class="kv-row" [class.border-bottom]="!last">
                    <span class="kv-label">{{ kv.key }}</span>
                    <span class="kv-value user-select-all">{{ kv.value }}</span>
                  </div>
                }
              }

              <!-- Action items -->
              @if (section.actionItems.length > 0) {
                <ol class="mb-0 ps-3 action-list">
                  @for (item of section.actionItems; track item.text) {
                    <li>{{ item.text }}</li>
                  }
                </ol>
              }

              <!-- Plain text -->
              @if (section.keyValues.length === 0 && section.actionItems.length === 0) {
                <div class="plain-content">{{ section.content }}</div>
              }
            </div>
          }
        </div>
      }
    }
  `,
  styles: [`
    .section-card {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      overflow: hidden;
      transition: box-shadow 0.15s ease;
    }

    .section-card.expanded {
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    }

    .section-header {
      padding: 0.625rem 1rem;
      cursor: pointer;
      transition: background-color 0.1s ease;
    }

    .section-header:hover {
      background-color: #f8fafc;
    }

    .section-title {
      font-size: 0.8rem;
      font-weight: 600;
      color: #475569;
    }

    .toggle-icon {
      color: #94a3b8;
      font-size: 0.9rem;
      font-weight: 500;
      width: 20px;
      text-align: center;
    }

    .section-body {
      padding: 0 1rem 0.75rem;
      border-top: 1px solid #f1f5f9;
    }

    .kv-row {
      display: flex;
      padding: 0.375rem 0;
      border-bottom: 1px solid #f8fafc;
      gap: 0.75rem;
    }

    .kv-label {
      width: 200px;
      min-width: 140px;
      flex-shrink: 0;
      font-size: 0.75rem;
      font-weight: 500;
      color: #94a3b8;
    }

    .kv-value {
      flex: 1;
      font-size: 0.8125rem;
      color: #1e293b;
      word-break: break-word;
      min-width: 0;
    }

    .action-list {
      font-size: 0.8125rem;
      line-height: 1.6;
      color: #334155;
    }

    .action-list li {
      margin-bottom: 0.25rem;
    }

    .plain-content {
      font-size: 0.8125rem;
      line-height: 1.6;
      color: #334155;
      white-space: pre-wrap;
    }
  `],
})
export class ParsedDescriptionComponent {
  parsed = input.required<ParsedDescription>();

  // First section starts expanded
  private expandedSections = signal<Set<number>>(new Set([0]));

  isExpanded(index: number): boolean {
    return this.expandedSections().has(index);
  }

  toggleSection(index: number): void {
    this.expandedSections.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }
}
